import { tap } from 'rxjs/operators';
// Angular
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT, Location } from '@angular/common';
import { Router } from '@angular/router';
import { Title } from "@angular/platform-browser";

import { ITab } from '../../model/ITab';

declare const wenji: {
    appList: string[];
    (key: AfidiumApps): IGlobalAppConfig;
};

export enum AfidiumApps {
    SKN = 'synchronyme',
    LCBK = 'locobook',
    IQBH = 'iaqobohotel',
    SASHA = 'sasha',
    DASHBOARD = 'dashboard',
    WATABILIGHT = 'watabilight',
    // STOCK = 'osman', not referenced yet in appList
    // WTB = 'watabi', not referenced yet in appList
}

export interface ICheckRightUse {
    code: string;
    permission: string;
}

export interface IActionLink {
    position: string;
    name: string;
    label: string;
    application: string;
}

export interface IGlobalAppConfig {
    code: string;
    name?: string;
    icon?: string;
    isGlobalNavigationHidden?: boolean;
    routerLink: string;
    tabs?: ITab[];
    secondaryName?: string;
    canEditTitle?: boolean;
    actionLink?: IActionLink[];
    isShowPriceSwitcher?: boolean;
}

@Injectable()
export class AppRootService {

    public appMenuPermission: ICheckRightUse[] = [
        {
            code: AfidiumApps.DASHBOARD,
            permission: "dashboard.use"
        },
    ];
    public readonly appConfigurations: Map<string, IGlobalAppConfig> = new Map();

    private current: string = null;


    constructor(
        private readonly location: Location,
        private readonly router: Router,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly titleService: Title,
    ) {
        // (wenji.appList || [])
        //     .map((code: string): string => code.replace(/^app_/, ''))
        //     .filter((code: string): boolean => code !== 'home')
        //     .forEach((code: string): void => {
        //         this.appConfigurations.set(code, this.getAppConfig(code));
        //     });

        this.router.events.pipe(
            tap((): void => {
                const current: string = this.getCurrentAppFromLocation();
                if (this.current !== current) {
                    this.initCurrentApp(current);
                }
            }))
            .subscribe();
    }

    public get currentAppConfig(): IGlobalAppConfig {
        return this.appConfigurations.get(this.current);
    }

    public get applicationConfigurations(): IGlobalAppConfig[] {
        return Array.from(this.appConfigurations.values());
    }

    public setCurrent(code: string): void {
        this.current = code;
    }

    public getAppConfig(code: string): IGlobalAppConfig {
        const appConfig: IGlobalAppConfig = {
            routerLink: `/${code}`,
            ...wenji[code],
            ...this.appConfigurations.get(code),
            code,
        };

        const currentRoute: string = this.router.url;
        if (appConfig.tabs) {
            appConfig.tabs = appConfig.tabs
                .map((tab: ITab): ITab => ({
                    ...{
                        ...tab,
                        tabs: (tab.tabs || [])
                            .map((tabChild: ITab): ITab => ({
                                ...tabChild,
                                active: currentRoute.startsWith(tabChild.tabDetector)
                            }))
                    },
                    active: currentRoute.startsWith(tab.tabDetector)
                }));
        }

        return appConfig;
    }

    public getCurrentAppFromLocation(): string {
        const currentLocationPath: string = this.location.path();
        // locationPath is the currentLocation got from AppNavComponent || AppHeaderComponent
        // equals to '/{current app name}/{current route}/ ex : '/sasha/home/'
        return currentLocationPath.split('/')[1];
    }

    public setAppConfig(config: IGlobalAppConfig): void {
        this.appConfigurations.set(config.code, config);
        this.initCurrentApp(this.current);
    }

    public setBrowserTitle(pageTitle?: string): void {
        if (pageTitle) {
            this.titleService.setTitle(pageTitle);
        } else {
            const title: string = this.getAppConfig(this.current).secondaryName ?
                `${this.getAppConfig(this.current).secondaryName} - ${this.getAppConfig(this.current).name}` :
                this.getAppConfig(this.current).name;
            this.titleService.setTitle(title);
        }
    }

    private initCurrentApp(currentApp: string): void {
        this.current = currentApp;
        this.appConfigurations.set(currentApp, this.getAppConfig(currentApp));
    }
}
